import type {
  TRIEnvironmentalMediumCodeType,
  TRIOnsiteReleaseQuantityDataType,
  TRIQuantityBasisEstimationCodeType,
  TRIWasteQuantityDataType,
} from '../../types/form-r.ts';
import type { FormRLeafLine } from './form-r-sections.ts';
import {
  formRBasisFromTransfers,
  formRNumbersToSection8Value,
  type ReleasesByChemicalAndFormRLine,
  transfersToXmlCongeners,
} from './form-r-utils.ts';
import type { CasOrCategory } from '../../types/db.ts';
import type {
  QuestionnaireAnswers,
  QuestionnaireStream,
  StreamID,
  WasteTransferStream,
} from '../../types/questionnaires.ts';
import type { Warning } from '../../types/tri.ts';
import type { Section8Total } from './form-r-section-8-sums.ts';
import type { TransferComputed } from '../releases-calculate.ts';

interface FormRSection5Args {
  chemicalName: string;
  casOrCategory: CasOrCategory;
  releasesByChemical: ReleasesByChemicalAndFormRLine;
  wasteTransfers: QuestionnaireAnswers['wasteTransfers'];
  streams: QuestionnaireAnswers['streams'];
  warnings: Warning[];
}

export const formRSection5OnsiteUicDisposalLines: readonly FormRLeafLine[] = ['5.4.1', '5.5.1A', '5.5.1B'];

export const formRSection5OnsiteOtherDisposalLines: readonly FormRLeafLine[] = [
  '5.1',
  '5.2',
  '5.3',
  '5.4.2',
  '5.5.2',
  '5.5.3A',
  '5.5.3B',
  '5.5.4',
];

export function formRSection5OnSiteReleases(args: FormRSection5Args): {
  OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[];
  OnsiteUICDisposalQuantity: Section8Total;
  OnsiteOtherDisposalQuantity: Section8Total;
} {
  const simple = formRSection5Simple(args);
  const water = formRSection5ReleasesToWater(args);

  return {
    OnsiteReleaseQuantity: simple.OnsiteReleaseQuantity.concat(water.OnsiteReleaseQuantity),
    OnsiteUICDisposalQuantity: formRNumbersToSection8Value(simple.OnsiteUICDisposalQuantity),
    OnsiteOtherDisposalQuantity: formRNumbersToSection8Value([
      ...simple.OnsiteOtherDisposalQuantity,
      ...water.OnsiteOtherDisposalQuantity,
    ]),
  };
}

export const formRWasteQuantityNA: TRIWasteQuantityDataType = {
  Quantity: {
    choice: 'NA',
    value: true,
  },
  QuantityBasis: {
    choice: 'NA',
    value: true,
  },
  ToxicEquivalencyIdentification: null,
  NonProductionWasteQuantityMeasure: null, // TODO: 6. Form R - May need to add support for Section 8.8 at some point.
};

// Everything except 5.3 can be handled the same:
function formRSection5Simple({ chemicalName, casOrCategory, releasesByChemical, warnings }: FormRSection5Args): {
  OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[];
  OnsiteUICDisposalQuantity: Section8Total[];
  OnsiteOtherDisposalQuantity: Section8Total[];
} {
  const OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[] = [];
  const OnsiteUICDisposalQuantity: Section8Total[] = [];
  const OnsiteOtherDisposalQuantity: Section8Total[] = [];

  const sectionFiveLinesAndCodes: {
    line: FormRLeafLine;
    code: TRIEnvironmentalMediumCodeType;
  }[] = [
    { line: '5.1', code: 'AIR FUG' },
    { line: '5.2', code: 'AIR STACK' },
    { line: '5.4.1', code: 'UNINJ I' },
    { line: '5.4.2', code: 'UNINJ IIV' },
    { line: '5.5.1A', code: 'RCRA C' },
    { line: '5.5.1B', code: 'OTH LANDF' },
    { line: '5.5.2', code: 'LAND TREA' },
    { line: '5.5.3A', code: 'SI 5.5.3A' },
    { line: '5.5.3B', code: 'SI 5.5.3B' },
    { line: '5.5.4', code: 'OTH DISP' },
  ];
  for (const { line, code: EnvironmentalMediumCode } of sectionFiveLinesAndCodes) {
    const sectionFiveReleases = releasesByChemical.get(`${casOrCategory}-${line}`);
    if (sectionFiveReleases) {
      const ToxicEquivalencyIdentification = transfersToXmlCongeners(
        casOrCategory,
        line,
        sectionFiveReleases.transfers,
        warnings,
      );
      const section8Total: Section8Total = {
        TotalQuantity: sectionFiveReleases.amountReleased,
        ToxicEquivalencyIdentification,
      };

      if (formRSection5OnsiteUicDisposalLines.includes(line)) {
        OnsiteUICDisposalQuantity.push(section8Total);
      } else {
        OnsiteOtherDisposalQuantity.push(section8Total);
      }

      OnsiteReleaseQuantity.push({
        EnvironmentalMediumCode,
        OnsiteWasteQuantity: {
          Quantity: {
            choice: 0,
            value: {
              WasteQuantityMeasure: sectionFiveReleases.amountReleased,
              WasteQuantityCatastrophicMeasure: null,
            },
          },
          QuantityBasis: {
            choice: 'QuantityBasisEstimationCode',
            value: formRBasisFromTransfers(sectionFiveReleases.transfers, {
              chemicalName,
              casOrCategory,
              formRLine: line,
              warnings,
            }),
          },
          ToxicEquivalencyIdentification,
          NonProductionWasteQuantityMeasure: null, // TODO: 6. Form R - May need to add support for Section 8.8 at some point.
        },
        WaterStream: null, // Not applicable to air and disposal emissions.
      });
    } else {
      OnsiteReleaseQuantity.push({
        EnvironmentalMediumCode,
        OnsiteWasteQuantity: formRWasteQuantityNA,
        WaterStream: null, // Not applicable to air and disposal emissions.
      });
    }
  }

  return {
    OnsiteReleaseQuantity,
    OnsiteUICDisposalQuantity,
    OnsiteOtherDisposalQuantity,
  };
}

function formRSection5ReleasesToWater({
  chemicalName,
  casOrCategory,
  releasesByChemical,
  wasteTransfers,
  streams,
  warnings,
}: FormRSection5Args): {
  OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[];
  OnsiteOtherDisposalQuantity: Section8Total[];
} {
  const OnsiteReleaseQuantity: TRIOnsiteReleaseQuantityDataType[] = [];
  const OnsiteOtherDisposalQuantity: Section8Total[] = [];

  const sectionFiveThreeReleases = releasesByChemical.get(`${casOrCategory}-5.3`);
  if (sectionFiveThreeReleases) {
    const transfersGrouped = new Map<
      `${StreamID}-${TRIQuantityBasisEstimationCodeType}`,
      {
        transferCalc: TransferComputed;
        transferInputs: WasteTransferStream;
        basis: TRIQuantityBasisEstimationCodeType;
        stream: QuestionnaireStream;
      }[]
    >();

    for (const transferCalc of sectionFiveThreeReleases.transfers) {
      let transferInputs = wasteTransfers[transferCalc.transferID];
      if (!transferInputs || transferInputs.type !== 'stream') {
        warnings.push({
          warningID: 'TW1008',
          type: 'questionnaire',
          subType: transferCalc.transferID,
          casOrCategory,
          message: `${chemicalName} (${casOrCategory}), we were unable to find the associated transfer details from the questionnaire (${transferCalc.transferID}).`,
          blocksPriorYearSection8Calculation: true,
        });
        transferInputs = {
          transferID: transferCalc.transferID,
          wasteStreamID: 'waste_stream_missing',
          allocationOrAnswer: '',
          type: 'stream',
          streamID: 'stream_missing',
        };
      }

      const basis = formRBasisFromTransfers([transferCalc], {
        chemicalName,
        casOrCategory,
        formRLine: '5.3',
        warnings,
      });

      let stream = streams[transferInputs.streamID as StreamID];
      if (!stream) {
        warnings.push({
          warningID: 'TW1009',
          type: 'questionnaire',
          subType: transferCalc.transferID,
          casOrCategory,
          message: `${chemicalName} (${casOrCategory}), we were unable to find the associated stream details from the questionnaire (${transferInputs.streamID}).`,
          blocksPriorYearSection8Calculation: true,
        });
        stream = {
          streamID: transferInputs.streamID as StreamID,
          name: 'UNKNOWN STREAM',
          reachCode: 'UNKNOWN REACH CODE',
        };
      }

      const key = `${stream.streamID}-${basis}` as const;
      if (!transfersGrouped.has(key)) {
        transfersGrouped.set(key, []);
      }
      transfersGrouped.get(key)!.push({
        transferCalc,
        transferInputs,
        basis,
        stream,
      });
    }

    let WaterSequenceNumber = 0;
    for (const group of transfersGrouped.values()) {
      WaterSequenceNumber += 1;

      let amountReleased = 0;
      let fromStormwater = 0;
      for (const { transferCalc } of group) {
        amountReleased += transferCalc.amountReleased;
        if (transferCalc.isFromStormwater) {
          fromStormwater += transferCalc.amountReleased;
        }
      }
      let stormwaterPercentage: number;
      if (amountReleased > 0) {
        stormwaterPercentage = (fromStormwater / amountReleased) * 100;
      } else {
        // If releases are zero, use the first transfer:
        stormwaterPercentage = group.values().next().value?.transferCalc.isFromStormwater ? 100 : 0;
      }

      const ToxicEquivalencyIdentification = transfersToXmlCongeners(
        casOrCategory,
        '5.3',
        group.map((g) => g.transferCalc),
        warnings,
      );
      OnsiteOtherDisposalQuantity.push({
        TotalQuantity: amountReleased,
        ToxicEquivalencyIdentification,
      });

      const { basis, stream } = group[0];

      if (stream.reachCode && !/^\d{14}$/.test(stream.reachCode)) {
        warnings.push({
          warningID: 'TW1122',
          type: 'questionnaire',
          subType: stream.streamID,
          casOrCategory,
          message: `Reach codes need to be 14 digits long, but got "${stream.reachCode}" for "${stream.name}".`,
          blocksPriorYearSection8Calculation: false,
        });
      }

      OnsiteReleaseQuantity.push({
        EnvironmentalMediumCode: 'WATER',
        OnsiteWasteQuantity: {
          Quantity: {
            choice: 0,
            value: {
              WasteQuantityMeasure: amountReleased,
              WasteQuantityCatastrophicMeasure: null,
            },
          },
          QuantityBasis: {
            choice: 'QuantityBasisEstimationCode',
            value: basis,
          },
          ToxicEquivalencyIdentification,
          NonProductionWasteQuantityMeasure: null, // TODO: 6. Form R - May need to add support for Section 8.8 at some point.
        },
        WaterStream: {
          WaterSequenceNumber,
          StormWaterPercent: {
            choice: 'ReleaseStormWaterPercent',
            value: stormwaterPercentage,
          },
          StreamName: stream.name,
          StreamReachCode: stream.reachCode,
        },
      });
    }
  } else {
    OnsiteReleaseQuantity.push({
      EnvironmentalMediumCode: 'WATER',
      OnsiteWasteQuantity: formRWasteQuantityNA,
      WaterStream: null, // No stream to specify if water releases are not applicable.
    });
  }

  return {
    OnsiteReleaseQuantity,
    OnsiteOtherDisposalQuantity,
  };
}
